import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  keyframes,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EmptyState, LoadingBox } from "@/components/ui";
import { useApiMutation, type WithDynamicUrl } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import type { TestAgentV2Session } from "../types/test-agent-2.ts";
import type { Test, TestContext } from "../types.ts";
import { VideoPlayer } from "./video-player.tsx";

// Swipe animations
const swipeLeft = keyframes`
  0% { transform: translateX(0) rotate(0deg); opacity: 1; }
  100% { transform: translateX(-150%) rotate(-20deg); opacity: 0; }
`;

const swipeRight = keyframes`
  0% { transform: translateX(0) rotate(0deg); opacity: 1; }
  100% { transform: translateX(150%) rotate(20deg); opacity: 0; }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: scale(0.95); }
  100% { opacity: 1; transform: scale(1); }
`;

type Props = {
  discoveryRunId: string;
  agentIds?: string[];
  maxWidth?: string | number;
};

type SwipeDirection = "left" | "right" | null;

export const TestReviewSwiper = ({ discoveryRunId, maxWidth }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState<number[]>([]);
  const [swipeDirection, setSwipeDirection] = useState<SwipeDirection>(null);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [hoveredStepId, setHoveredStepId] = useState<string | null>(null);

  // Fetch tests from the discovery run
  const {
    data: allTests,
    isLoading,
    refetch,
  } = useApiQuery<Test[]>(`/tests?source_id=${discoveryRunId}&source=discovery`);

  const currentTestId = useMemo(() => {
    const currentTest = allTests ? allTests[currentIndex] : null;
    return currentTest ? currentTest.id : null;
  }, [allTests, currentIndex]);

  // Fetch all agent sessions to get their recordings
  const { data: agentSession, isLoading: agentSessionLoading } = useApiQuery<TestAgentV2Session>(
    `/test-agent-v2-sessions/${currentTestId}`,
    {
      enabled: !!currentTestId,
    },
  );

  // Fetch contexts for the current test
  const {
    data: currentTestContexts,
    isLoading: isLoadingContexts,
    refetch: refetchContexts,
  } = useApiQuery<TestContext[]>(`/tests/${currentTestId}/context`, {
    enabled: !!currentTestId,
  });

  // All tests including reviewed ones (show status via tags)
  const tests = useMemo(() => {
    return allTests || [];
  }, [allTests]);

  const currentTest = tests[currentIndex];

  // Get the recording path for the current test
  const currentRecordingPath = useMemo(() => {
    return agentSession?.data.recording_path || null;
  }, [agentSession?.data.recording_path]);

  // Count reviewed tests (accepted or rejected)
  const reviewedCount = useMemo(() => {
    return (
      tests.filter((t) => t.tags?.includes("accepted") || t.tags?.includes("rejected")).length || 0
    );
  }, [tests]);

  const progress = tests.length > 0 ? ((reviewedCount / tests.length) * 100).toFixed(0) : 0;

  // Check if current test is already reviewed
  const isCurrentTestReviewed =
    currentTest?.tags?.includes("accepted") || currentTest?.tags?.includes("rejected");

  // Mutation for adding tags (generic, uses _url)
  const { mutate: addTag, isPending: isAddingTag } = useApiMutation<
    { tags: string[] },
    { success: boolean }
  >("POST", "", {
    onError: (error) => {
      enqueueSnackbar(`Failed to update test: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Mutation for removing tags
  const { mutate: removeTag, isPending: isRemovingTag } = useApiMutation<
    { tags: string[] },
    { success: boolean }
  >("DELETE", "", {
    onError: (error) => {
      enqueueSnackbar(`Failed to update test: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Mutation for updating context (toggle disable)
  const { mutate: updateContext, isPending: isUpdatingContext } = useApiMutation<
    WithDynamicUrl<Partial<TestContext>>,
    TestContext
  >("PUT", "", {
    onSuccess: () => {
      refetchContexts();
      enqueueSnackbar("Step updated", {
        variant: "success",
        autoHideDuration: 2000,
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update step: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Mutation for deleting context
  const { mutate: deleteContext, isPending: isDeletingContext } = useApiMutation<
    WithDynamicUrl<void>,
    void
  >("DELETE", "", {
    onSuccess: () => {
      refetchContexts();
      enqueueSnackbar("Step deleted", {
        variant: "success",
        autoHideDuration: 2000,
      });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to delete step: ${error.message}`, {
        variant: "error",
      });
    },
  });

  const isUpdating = isAddingTag || isRemovingTag;
  const isContextMutating = isUpdatingContext || isDeletingContext;

  const animateAndNext = useCallback((direction: SwipeDirection, callback: () => void) => {
    setSwipeDirection(direction);
    setTimeout(() => {
      callback();
      setSwipeDirection(null);
    }, 300);
  }, []);

  const handleNext = useCallback(() => {
    if (currentIndex < tests.length - 1) {
      setHistory((prev) => [...prev, currentIndex]);
      setCurrentIndex((prev) => prev + 1);
    } else if (tests.length > 0) {
      // Wrap around to beginning
      setHistory((prev) => [...prev, currentIndex]);
      setCurrentIndex(0);
      setIsReviewMode(false);
    }
  }, [currentIndex, tests.length]);

  const handleAccept = useCallback(() => {
    if (!currentTest) return;

    const doAccept = () => {
      addTag(
        { _url: `/tests/${currentTest.id}/tags`, tags: ["accepted"] },
        {
          onSuccess: () => {
            enqueueSnackbar("Test accepted", { variant: "success" });
            refetch();
            animateAndNext("right", handleNext);
          },
        },
      );
    };

    // If previously rejected, remove that tag first
    if (currentTest.tags?.includes("rejected")) {
      removeTag(
        { _url: `/tests/${currentTest.id}/tags`, tags: ["rejected"] },
        { onSuccess: doAccept },
      );
    } else {
      doAccept();
    }
  }, [currentTest, addTag, removeTag, enqueueSnackbar, refetch, animateAndNext, handleNext]);

  const handleReject = useCallback(() => {
    if (!currentTest) return;

    const doReject = () => {
      addTag(
        { _url: `/tests/${currentTest.id}/tags`, tags: ["rejected"] },
        {
          onSuccess: () => {
            enqueueSnackbar("Test rejected", { variant: "warning" });
            refetch();
            animateAndNext("left", handleNext);
          },
        },
      );
    };

    // If previously accepted, remove that tag first
    if (currentTest.tags?.includes("accepted")) {
      removeTag(
        { _url: `/tests/${currentTest.id}/tags`, tags: ["accepted"] },
        { onSuccess: doReject },
      );
    } else {
      doReject();
    }
  }, [currentTest, addTag, removeTag, enqueueSnackbar, refetch, animateAndNext, handleNext]);

  const handleSkip = useCallback(() => {
    handleNext();
  }, [handleNext]);

  const handleBack = useCallback(() => {
    if (history.length > 0) {
      const prevIndex = history[history.length - 1];
      setHistory((prev) => prev.slice(0, -1));
      setCurrentIndex(prevIndex);
    }
  }, [history]);

  const handleStartOver = useCallback(() => {
    setCurrentIndex(0);
    setHistory([]);
    setIsReviewMode(true);
  }, []);

  const handleToggleDisable = useCallback(
    (ctx: TestContext) => {
      if (!currentTest) return;
      updateContext({
        _url: `/tests/${currentTest.id}/context/${ctx.id}`,
        disabled: !ctx.disabled,
      });
    },
    [currentTest, updateContext],
  );

  const handleDeleteStep = useCallback(
    (ctx: TestContext) => {
      if (!currentTest) return;
      deleteContext({
        _url: `/tests/${currentTest.id}/context/${ctx.id}`,
      });
    },
    [currentTest, deleteContext],
  );

  // Reset accordion when changing tests
  useEffect(() => {
    setAccordionExpanded(false);
  }, []);

  // Check if all tests are reviewed
  const allReviewed = useMemo(() => {
    return reviewedCount === tests.length || history.length === tests.length;
  }, [tests.length, reviewedCount, history]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Don't trigger if animation is in progress or updating
      if (swipeDirection !== null || isUpdating) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case "b":
          handleBack();
          break;
        case "r":
        case "arrowleft":
          if (!currentTest?.tags?.includes("rejected")) {
            handleReject();
          }
          break;
        case "s":
          handleSkip();
          break;
        case "a":
        case "arrowright":
          if (!currentTest?.tags?.includes("accepted")) {
            handleAccept();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleBack, handleReject, handleSkip, handleAccept, swipeDirection, isUpdating, currentTest]);

  const acceptedCount = useMemo(() => {
    return tests.filter((t) => t.tags?.includes("accepted")).length;
  }, [tests]);

  const rejectedCount = useMemo(() => {
    return tests.filter((t) => t.tags?.includes("rejected")).length;
  }, [tests]);

  if (isLoading) {
    return <LoadingBox sx={{ py: 4, p: 0 }} />;
  }

  if (!allTests || allTests.length === 0) {
    return <EmptyState message="No tests found for this discovery run." />;
  }

  if (!currentTest) {
    return <EmptyState message="No test to display." />;
  }

  // Show completion message when all tests are reviewed (and not in review mode)
  if (allReviewed && !isReviewMode) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={4}
        height="100%"
        width="100%"
      >
        <Typography variant="h4" gutterBottom>
          🎉
        </Typography>
        <Typography variant="h5" color="success.main" gutterBottom>
          You're done!
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          You've reviewed all {tests.length} tests, congratulations!
        </Typography>
        <Stack direction="row" spacing={2} mt={1} mb={3}>
          <Chip
            label={`${acceptedCount} accepted`}
            color="success"
            size="medium"
            variant="filled"
          />
          <Chip label={`${rejectedCount} rejected`} color="error" size="medium" variant="filled" />
        </Stack>
        <Button variant="text" onClick={handleStartOver}>
          Have second doubts? Review Again
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: maxWidth || "100%", mx: "auto" }}>
      {/* Progress */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" width="100%" maxWidth="220px" mt={1}>
          Review Generated Tests
        </Typography>
        <Stack direction="column" width="100%" spacing={0.5}>
          <Stack direction="row" justifyContent="flex-end" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              {reviewedCount} / {tests.length} reviewed ({progress}%)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Number(progress)}
            sx={{ height: 6, borderRadius: 1 }}
          />
        </Stack>
      </Stack>

      <Divider />

      {/* Current Test Card */}
      <Box
        sx={{
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Card
          key={currentTest.id}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 1,
            border: isCurrentTestReviewed ? "2px solid" : "1px solid",
            borderColor: isCurrentTestReviewed
              ? currentTest.tags?.includes("accepted")
                ? "success.main"
                : "error.main"
              : "rgba(0, 0, 0, 0.12)",
            animation: swipeDirection
              ? `${swipeDirection === "left" ? swipeLeft : swipeRight} 0.4s ease-out forwards`
              : `${fadeIn} 0.4s ease-out`,
          }}
        >
          <CardContent sx={{ flex: 1, overflow: "auto" }}>
            <Stack spacing={2}>
              {/* Test name and tags */}
              <Stack spacing={2} direction="column">
                <Typography variant="h6" gutterBottom>
                  {currentTest.name}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {currentTest.tags?.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant={tag === "accepted" || tag === "rejected" ? "filled" : "outlined"}
                      color={
                        tag === "accepted" ? "success" : tag === "rejected" ? "error" : "default"
                      }
                    />
                  ))}
                  {currentTest.is_positive !== undefined && (
                    <Chip
                      label={currentTest.is_positive ? "Positive" : "Negative"}
                      size="small"
                      color={currentTest.is_positive ? "success" : "error"}
                      variant="outlined"
                    />
                  )}
                  {currentTest.severity && (
                    <Chip label={currentTest.severity} size="small" variant="outlined" />
                  )}
                  {currentTest.priority && (
                    <Chip label={currentTest.priority} size="small" variant="outlined" />
                  )}
                </Stack>
                {/* Recording */}
                {!currentRecordingPath && agentSessionLoading && (
                  <LoadingBox sx={{ height: "320px" }} />
                )}
                {currentRecordingPath && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      maxWidth: maxWidth ?? "600px",
                      flexGrow: 1,
                      alignSelf: "center",
                    }}
                  >
                    <VideoPlayer
                      src={currentRecordingPath}
                      width="100%"
                      height="320px"
                      playbackRate={16}
                      controls={true}
                      autoPlay={true}
                      muted={true}
                      loop
                    />
                  </Box>
                )}
              </Stack>

              {/* Success criteria */}
              {currentTest.success_criteria && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Success Criteria
                  </Typography>
                  <Typography variant="body2">{currentTest.success_criteria}</Typography>
                </Box>
              )}

              {/* Reasoning */}
              {currentTest.reasoning && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reasoning
                  </Typography>
                  <Typography variant="body2">{currentTest.reasoning}</Typography>
                </Box>
              )}

              {/* Step Details Accordion */}
              {currentTestContexts && currentTestContexts.length > 0 && (
                <Accordion
                  expanded={accordionExpanded}
                  onChange={(_, expanded) => setAccordionExpanded(expanded)}
                  sx={{
                    mt: 2,
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "6px !important",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: "40px",
                      "& .MuiAccordionSummary-content": { my: 0.5 },
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Test Steps ({currentTestContexts.length})
                      {isLoadingContexts && <CircularProgress size={12} sx={{ ml: 1 }} />}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {currentTestContexts.map((ctx, index) => (
                        <Box
                          key={ctx.id}
                          onMouseEnter={() => setHoveredStepId(ctx.id)}
                          onMouseLeave={() => setHoveredStepId(null)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 0.75,
                            px: 1.5,
                            borderRadius: "4px",
                            backgroundColor: ctx.disabled ? "rgba(0, 0, 0, 0.02)" : "transparent",
                            border: "1px solid",
                            borderColor: "divider",
                            transition: "all 0.15s ease-in-out",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.03)",
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "text.secondary",
                                flexShrink: 0,
                              }}
                            >
                              #{index + 1}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                color: ctx.disabled ? "text.secondary" : "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textDecoration: ctx.disabled ? "line-through" : "none",
                              }}
                            >
                              {ctx.name}
                            </Typography>
                          </Stack>

                          {/* Action buttons - visible on hover */}
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                              opacity: hoveredStepId === ctx.id ? 1 : 0,
                              transition: "opacity 0.15s ease-in-out",
                            }}
                          >
                            <Tooltip title={ctx.disabled ? "Enable" : "Disable"}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleDisable(ctx);
                                }}
                                disabled={isContextMutating}
                                sx={{
                                  padding: "4px",
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "primary.main",
                                    backgroundColor: "rgba(22, 0, 234, 0.08)",
                                  },
                                }}
                              >
                                {ctx.disabled ? (
                                  <ToggleOffIcon sx={{ fontSize: "16px" }} />
                                ) : (
                                  <ToggleOnIcon sx={{ fontSize: "16px" }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteStep(ctx);
                                }}
                                disabled={isContextMutating}
                                sx={{
                                  padding: "4px",
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "error.main",
                                    backgroundColor: "rgba(220, 53, 69, 0.08)",
                                  },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Stack direction="column" spacing={2} alignItems="center">
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={history.length === 0 || isUpdating || swipeDirection !== null}
          >
            Back (B)
          </Button>
          <Button
            variant={currentTest.tags?.includes("rejected") ? "contained" : "outlined"}
            color="error"
            size="large"
            startIcon={<CloseIcon />}
            onClick={handleReject}
            disabled={
              isUpdating || currentTest.tags?.includes("rejected") || swipeDirection !== null
            }
            sx={{ minWidth: 100 }}
          >
            Reject (R/←)
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            startIcon={<SkipNextIcon />}
            onClick={handleSkip}
            disabled={isUpdating || swipeDirection !== null}
          >
            Skip (S)
          </Button>
          <Button
            variant={currentTest.tags?.includes("accepted") ? "contained" : "outlined"}
            color="success"
            size="large"
            startIcon={<CheckIcon />}
            onClick={handleAccept}
            disabled={
              isUpdating || currentTest.tags?.includes("accepted") || swipeDirection !== null
            }
            sx={{ minWidth: 100 }}
          >
            Accept (A/→)
          </Button>
        </Stack>

        {/* Counter */}
        <Typography variant="caption" color="text.secondary" textAlign="center">
          Test {currentIndex + 1} of {tests.length}
          {isCurrentTestReviewed && (
            <span>
              {" "}
              — Already {currentTest.tags?.includes("accepted") ? "accepted" : "rejected"}
            </span>
          )}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default TestReviewSwiper;
